import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAgentStatusResponse, IJwtData, IRefreshAccessToken } from "@interfaces";
import { RootState } from ".";
import { authApi } from "@api";
import { lsAccount, lsJwt } from "@utilities";
import _ from "lodash";

interface IRefreshTokenPayload {
    refreshToken: string;
    userId: string;
}

export const refreshAccessToken = createAsyncThunk<IRefreshAccessToken, IRefreshTokenPayload>(
    "auth/refreshAccessToken",
    async (payload: IRefreshTokenPayload, { rejectWithValue }) => {
        try {
            const res = await authApi.refresh({
                refresh_token: payload.refreshToken,
                user_id: payload.userId,
            });
            if (res.data.code !== 200) {
                return rejectWithValue(res.data.message);
            }
            return res.data.data as IRefreshAccessToken;
        } catch (err: any) {
            const errorMessage = "Failed to refresh access token";
            console.error(errorMessage, err);
            return rejectWithValue(errorMessage);
        }
    },
);

interface IState {
    jwt: IJwtData | null;
    agentInfo: IAgentStatusResponse | null;
    isLoading: boolean;
    error: string | null;
}

const rawJwt = lsJwt.getItem();
const localJwt: IJwtData | null =
    _.isNil(rawJwt) || _.isEmpty(rawJwt) ? null : (rawJwt as IJwtData);

const initialState: IState = {
    agentInfo: null,
    jwt: localJwt,
    isLoading: false,
    error: null,
};

const authSlice = createSlice({
    name: "auth",
    initialState: initialState,
    reducers: {
        setAgentInfo: (state, action: PayloadAction<IAgentStatusResponse>) => {
            state.agentInfo = action.payload;
        },
        setJwt: (state, action: PayloadAction<IJwtData>) => {
            state.jwt = action.payload;
            state.isLoading = false;
        },
        removeJwt: (state) => {
            lsJwt.setItem(null);
            lsAccount.setItem(null);
            state.jwt = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(refreshAccessToken.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(refreshAccessToken.rejected, (state, action: PayloadAction<any>) => {
                state.error = action.payload || "Failed to refresh access token";
                state.isLoading = false;
            })
            .addCase(
                refreshAccessToken.fulfilled,
                (state, action: PayloadAction<IRefreshAccessToken>) => {
                    state.jwt!.accessToken = action.payload.accessToken;
                    state.isLoading = false;
                    lsJwt.setItem(state.jwt);
                },
            );
    },
});

export const selectAuth = (state: RootState) => state.auth;

export const { setJwt, removeJwt } = authSlice.actions;

export default authSlice.reducer;
